import _head from "lodash/head";
import React, { useEffect } from "react";
import environment from "web/script/modules/environment";
import Drawer from "web/react/components/drawer/drawer";
import FeedsFilters from "web/react/components/feeds-filters/feeds-filters";
import withReduxProvider from "web/react/redux-provider";
import { useCustomFeatures } from "web/react/hooks/use-custom-features/use-custom-features";
import { useDesktopBreakpoint } from "web/react/hooks/use-desktop-breakpoint/use-desktop-breakpoint";
import { gettext } from "web/script/modules/django-i18n";
import {
    UniversalFiltersData,
    isFilterSection,
    CategoryFilterOption,
} from "web/types/universal-filters";
import {
    usePageManager,
    PaginatedItem,
} from "web/react/components/feeds-filters/feeds-filters-pagination/pagination-state";
import { useFilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { useSidebarHandler } from "web/react/components/feeds-filters/hooks/use-sidebar-handler";

interface FilterContainerProps {
    filters: UniversalFiltersData;
}
export function FilterContainer({ filters }: FilterContainerProps): JSX.Element {
    const [isOpen, closeSidebar] = useSidebarHandler();

    let pageManager = usePageManager(gettext("general.filters"));

    const {
        popActivePage,
        pushActivePage,
        paginationState,
        popPage,
        resetPagination,
        title: overlayTitle,
    } = pageManager;

    let filterManager = useFilterManager(filters);
    const {
        applyState,
        canApply,
        cancelState,
        displayedFilters,
        updateFromServer,
        ...otherFilterProps
    } = filterManager;

    const hasSidebar = useDesktopBreakpoint();
    const treeStructure = environment.getFeature("uj_tree_filter_all_screens");

    useCustomFeatures(displayedFilters.features);

    useEffect(() => {
        let categorySection = _head(
            displayedFilters.filter_section_list.filter(
                (section) => section.id === "section-category"
            )
        );

        let hasTreeStructure = hasSidebar || treeStructure;

        if (categorySection && hasTreeStructure && displayedFilters.features.uj_open_category) {
            pushActivePage([categorySection]);
        }
        // We only ever want to run this hook once, the first time we have a filter_section_list
        // (some pages initialise with out a filter_section_list and then load it (e.g. /search/)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedFilters.filter_section_list.length == 0]);

    useEffect(() => {
        if (treeStructure && !hasSidebar) {
            updateFromServer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationState, treeStructure, hasSidebar]);

    function handleCollapse(item): void {
        popPage(item);
        updateFromServer();
    }

    function closeAndCancel(): void {
        closeSidebar();
        cancelState();
        resetPagination();
    }

    function handleApply(): void {
        closeSidebar();
        if (canApply) {
            applyState();
        }
        resetPagination();
    }

    function onBack(): void {
        if (treeStructure || !paginationState.hasActivePage()) {
            handleApply();
            return;
        }
        popActivePage();
        updateFromServer();
    }

    let pushMoreThenOneItem = treeStructure || hasSidebar;

    function handleItemClick(item: PaginatedItem): void {
        if (isFilterSection(item) && pushMoreThenOneItem && item.id == "section-category") {
            let options = item.option_groups[0].options as CategoryFilterOption[];
            let child = options.find((child) => child.checked);

            if (child) {
                pushActivePage([item, child]);
                return;
            }
        }
        pushActivePage([item]);
    }

    return (
        <Drawer
            title={treeStructure ? gettext("general.filters") : overlayTitle}
            id={"feeds-filter"}
            open={isOpen}
            onCancel={closeAndCancel}
            onBack={onBack}
            onOverlay={handleApply}
        >
            <FeedsFilters
                applyState={handleApply}
                canApply={canApply}
                displayedFilters={displayedFilters}
                onClose={handleCollapse}
                onPageChange={handleItemClick}
                paginationState={paginationState}
                {...otherFilterProps}
            />
        </Drawer>
    );
}

export default withReduxProvider(FilterContainer);
